import React, { useMemo, useState } from 'react';
import { FiAlertTriangle, FiEye, FiFilter, FiFolder, FiGrid, FiList, FiMoreVertical, FiPlus, FiStar, FiTrash2 } from 'react-icons/fi';
import { badgeOf, priceRange, setupPercent, taskCount, tiersOf } from './serviceCatalog';

function ratingNumber(service) {
  const n = parseFloat(service.rating);
  return Number.isFinite(n) ? n : 0;
}

export default function ServicesHome({
  services,
  urlSearch = '',
  onOpen,
  onAdd,
  selectMode,
  selectedIds,
  onToggleSelect,
  onStartSelect,
  onCancelSelect,
  onInactive,
  onReactivate,
  onDelete,
  onToggleActive,
  onDeleteOne,
  selectedActiveCount,
  selectedInactiveCount,
}) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [layout, setLayout] = useState('grid');
  const [menuId, setMenuId] = useState('');

  const categories = useMemo(() => {
    const names = new Set(services.map((row) => row.badge || badgeOf(row)).filter(Boolean));
    return ['All', ...Array.from(names).sort()];
  }, [services]);

  const visible = services.filter((row) => {
    if (category !== 'All' && (row.badge || badgeOf(row)) !== category) return false;
    const q = (query || urlSearch).trim().toLowerCase();
    if (!q) return true;
    return [row.name, row.description, row.code, row.category]
      .some((field) => String(field || '').toLowerCase().includes(q));
  });

  const activeCount = services.filter((row) => row.status !== 'inactive').length;
  const projectTotal = services.reduce((sum, row) => sum + (Number(row.totalProjects) || 0), 0);
  const rated = services.map(ratingNumber).filter((n) => n > 0);
  const average = rated.length
    ? (rated.reduce((sum, n) => sum + n, 0) / rated.length).toFixed(1)
    : '—';
  const templates = services.reduce((sum, row) => sum + taskCount(row), 0);

  const stats = [
    { value: services.length, label: 'Total Services', hint: `${activeCount} active` },
    { value: projectTotal, label: 'Total Projects', hint: 'this year' },
    { value: average, label: 'Avg Rating', hint: 'across all services' },
    { value: templates, label: 'Task Templates', hint: 'catalogue tasks' },
  ];
  const incomplete = visible.filter((row) => setupPercent(row) < 100);
  const cards = layout === 'grid';

  return (
    <div className="svc">
      <header className={`svc-head${cards ? ' svc-head--cards' : ''}`}>
        <div>
          <h1>{cards ? 'Services Management' : 'Services'}</h1>
          <p>
            {cards
              ? 'Define and manage your cleaning services, task catalogues and pricing'
              : 'Configure services once — reuse them across all quotations and projects'}
          </p>
        </div>
        <button type="button" className="svc-add" onClick={onAdd}>
          <FiPlus /> Add Service
        </button>
      </header>

      {cards ? (
        <>
          <div className="svc-steps">
            <span>Workflow</span>
            <strong>1. Services catalogue</strong>
            <em>2. Configure service</em>
            <em>Project</em>
          </div>
          <div className="svc-stats">
            {stats.map((card) => (
              <article key={card.label}>
                <strong>{card.value}</strong>
                <span>{card.label}</span>
                <small>{card.hint}</small>
              </article>
            ))}
          </div>
        </>
      ) : null}

      <div className="svc-toolbar">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search services by name or code..."
          aria-label="Search services"
        />
        <label className="svc-filter">
          <FiFilter />
          <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Filter by category">
            {categories.map((name) => (
              <option key={name} value={name}>
                {name === 'All' ? 'Filter by category' : name}
              </option>
            ))}
          </select>
        </label>
        <span className="svc-count">{visible.length} services</span>
        {cards ? (
          <button type="button" className="svc-add" onClick={onAdd}>
            <FiPlus /> Add Service
          </button>
        ) : null}
        <div className="svc-layout">
          <button type="button" className={layout === 'grid' ? 'is-on' : ''} onClick={() => setLayout('grid')} aria-label="Cards">
            <FiGrid />
          </button>
          <button type="button" className={layout === 'table' ? 'is-on' : ''} onClick={() => setLayout('table')} aria-label="Table">
            <FiList />
          </button>
        </div>
      </div>

      <div className="svc-selectbar">
        {!selectMode ? (
          <button type="button" onClick={onStartSelect}>Select</button>
        ) : (
          <>
            {selectedActiveCount > 0 ? (
              <button type="button" onClick={onInactive}>Make inactive ({selectedActiveCount})</button>
            ) : null}
            {selectedInactiveCount > 0 ? (
              <button type="button" onClick={onReactivate}>Reactivate ({selectedInactiveCount})</button>
            ) : null}
            {selectedIds.length > 0 ? (
              <button type="button" className="svc-select-del" onClick={onDelete}>
                <FiTrash2 /> Delete ({selectedIds.length})
              </button>
            ) : null}
            <button type="button" onClick={onCancelSelect}>Cancel</button>
          </>
        )}
      </div>

      {!cards && incomplete.length ? (
        <div className="svc-warn">
          <FiAlertTriangle />
          <span>
            <b>{incomplete.length} service{incomplete.length === 1 ? '' : 's'} incomplete setup:</b>{' '}
            {incomplete.map((row) => row.name).join(', ')}.
          </span>
          <button type="button" onClick={() => onOpen(incomplete[0])}>Fix now →</button>
        </div>
      ) : null}

      {visible.length === 0 ? (
        <p className="svc-empty">No services yet. Add one to start the catalogue.</p>
      ) : layout === 'grid' ? (
        <div className="svc-grid">
          {visible.map((service) => (
            <article
              key={service.id}
              className={`svc-card${service.status === 'inactive' ? ' is-off' : ''}`}
              onClick={() => (selectMode ? onToggleSelect(service.id) : onOpen(service))}
            >
              {selectMode ? (
                <input
                  type="checkbox"
                  checked={selectedIds.includes(service.id)}
                  onChange={() => onToggleSelect(service.id)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Select ${service.name}`}
                />
              ) : null}
              <div className="svc-card-photo">
                {service.hasImage || service.icon ? <img src={service.icon} alt="" /> : <span>{service.name.slice(0, 1)}</span>}
                <em className={service.status === 'inactive' ? 'is-off' : ''}>
                  {service.status === 'inactive' ? 'Inactive' : 'Active'}
                </em>
                {(service.badge || badgeOf(service)) ? <b>{service.badge || badgeOf(service)}</b> : null}
              </div>
              <div className="svc-card-body">
                <div className="svc-card-title">
                  <h3>{service.name}</h3>
                  {selectMode && selectedIds.includes(service.id) ? (
                    <button
                      type="button"
                      className="svc-icon svc-icon-del"
                      aria-label={`Delete ${service.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteOne?.(service);
                      }}
                    >
                      <FiTrash2 />
                    </button>
                  ) : (
                  <button
                    type="button"
                    className="svc-card-more"
                    aria-label={`${service.name} actions`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuId(menuId === service.id ? '' : service.id);
                    }}
                  >
                    <FiMoreVertical />
                  </button>
                  )}
                  {menuId === service.id && !(selectMode && selectedIds.includes(service.id)) ? (
                    <div className="svc-card-menu" onClick={(e) => e.stopPropagation()}>
                      <button type="button" onClick={() => onOpen(service)}>Open</button>
                      <button type="button" onClick={() => { setMenuId(''); onToggleActive?.(service); }}>
                        {service.status === 'inactive' ? 'Make active' : 'Make inactive'}
                      </button>
                      <button type="button" className="is-danger" onClick={() => { setMenuId(''); onDeleteOne?.(service); }}>
                        Delete
                      </button>
                    </div>
                  ) : null}
                </div>
                <small>{service.code}</small>
                <p>{String(service.description || '').trim() || 'No description yet.'}</p>
                <footer>
                  <span className="svc-card-rate"><FiStar /> {ratingNumber(service) ? ratingNumber(service).toFixed(1) : '—'}</span>
                  <span><FiFolder /> {service.totalProjects} projects</span>
                  <span><FiList /> {taskCount(service)} tasks</span>
                </footer>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="svc-table-wrap">
          <table className="svc-table">
            <thead>
              <tr>
                <th />
                <th>Service</th>
                <th>Task</th>
                <th>Visit</th>
                <th>Category</th>
                <th>Price range</th>
                <th>Status</th>
                <th>Projects</th>
                <th>Setup</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((service) => {
                const setup = setupPercent(service);
                return (
                  <tr key={service.id} onClick={() => onOpen(service)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(service.id)}
                        onChange={() => onToggleSelect(service.id)}
                        aria-label={`Select ${service.name}`}
                      />
                    </td>
                    <td>
                      <strong>{service.name}</strong>
                      <small>{service.code}</small>
                    </td>
                    <td><span className="svc-pill">{taskCount(service)}</span></td>
                    <td>{tiersOf(service).length || '—'}</td>
                    <td>{service.badge || badgeOf(service) || '—'}</td>
                    <td>{priceRange(service) || '—'}</td>
                    <td>
                      <em className={service.status === 'inactive' ? 'is-off' : ''}>
                        {service.status === 'inactive' ? 'Inactive' : 'Active'}
                      </em>
                    </td>
                    <td>{service.totalProjects}</td>
                    <td>
                      {setup >= 100 ? (
                        <em className="svc-done">Completed</em>
                      ) : setup < 50 ? (
                        <em className="svc-pending">Pending</em>
                      ) : (
                        <>
                          <span className="svc-setup"><i style={{ width: `${setup}%` }} /></span>
                          {setup}%
                        </>
                      )}
                    </td>
                    <td>
                      {selectedIds.includes(service.id) ? (
                        <button
                          type="button"
                          className="svc-icon svc-icon-del"
                          aria-label={`Delete ${service.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteOne?.(service);
                          }}
                        >
                          <FiTrash2 />
                        </button>
                      ) : null}
                      <button type="button" className="svc-eye" aria-label={`Open ${service.name}`} onClick={() => onOpen(service)}>
                        <FiEye />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
